/**
 * AgentAssistant — the reusable "Agent Dock" for any surface.
 *
 * A conversational panel (type or talk) pointed at a registry agent by key. It streams
 * live step-by-step progress (POST /agent-run/{key}/stream) and supports in-browser
 * streaming voice (falling back to browser speech). Drop `AgentAssistantPanel` at the top
 * of any page to give it an integrated (in-flow, collapsible) assistant for that page's
 * agent — NOT a floating widget, so it never collides with the support-chat launcher.
 *
 * This is the generic sibling of the Design Studio's DesignStudioDock (which additionally
 * shows a live page preview); this one is conversation-only, so it fits Pipeline,
 * Analytics, Campaigns, etc.
 */
import { useEffect, useRef, useState } from 'react';
import { Sparkles, Mic, Loader2, ArrowUp, Check, ShieldCheck, Square, ChevronDown } from 'lucide-react';
import { agentsApi } from '../services/platformApi';
import { tvTokens } from './triveny';
import { config } from '@/src/config';
import { BrowserVoiceSession, VoiceState } from '../../modules/web-presence/lib/browserVoice';
import { runAgentStream } from '../../modules/web-presence/lib/agentStream';

interface Msg {
  role: 'user' | 'agent';
  text: string;
  pending?: { tool: string }[];
}

export interface AgentAssistantProps {
  agentKey: string;
  title: string;
  greeting?: string;
  examples?: string[];
  /** Optional: called with a produced page_id (e.g. content agents). */
  onArtifact?: (pageId: string) => void;
}

function getSpeech(): any {
  if (typeof window === 'undefined') return null;
  return (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition || null;
}

export function AgentAssistant({ agentKey, title, greeting, examples = [], onArtifact }: AgentAssistantProps) {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Msg[]>(greeting ? [{ role: 'agent', text: greeting }] : []);
  const [busy, setBusy] = useState(false);
  const [liveSteps, setLiveSteps] = useState<{ tool: string; done: boolean; ok?: boolean }[]>([]);
  const [voiceState, setVoiceState] = useState<VoiceState | null>(null);
  const voiceRef = useRef<BrowserVoiceSession | null>(null);
  const recRef = useRef<any>(null);
  const [listening, setListening] = useState(false);
  const threadRef = useRef<HTMLDivElement>(null);
  const voiceActive = voiceState === 'connecting' || voiceState === 'live' || voiceState === 'listening' || voiceState === 'speaking';

  useEffect(() => () => voiceRef.current?.stop(), []);
  useEffect(() => {
    threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, busy, liveSteps]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    if (listening) recRef.current?.stop();
    setMessages((m) => [...m, { role: 'user', text }]);
    setInput('');
    setBusy(true);
    setLiveSteps([]);
    let gotSummary = false;
    try {
      await runAgentStream(agentKey, { instruction: text }, (ev) => {
        if (ev.type === 'step') {
          if (ev.status === 'running') setLiveSteps((s) => [...s, { tool: ev.tool, done: false }]);
          else
            setLiveSteps((s) => {
              const next = [...s];
              const idx = next.map((x) => x.tool).lastIndexOf(ev.tool);
              if (idx >= 0) next[idx] = { tool: ev.tool, done: true, ok: ev.ok };
              return next;
            });
        } else if (ev.type === 'artifact') {
          onArtifact?.(ev.page_id);
        } else if (ev.type === 'summary') {
          gotSummary = true;
          setMessages((m) => [...m, { role: 'agent', text: ev.summary || 'Done.', pending: ev.pending_approvals }]);
        } else if (ev.type === 'error') {
          setMessages((m) => [...m, { role: 'agent', text: `Sorry — ${ev.error}` }]);
        }
      });
      if (!gotSummary) setMessages((m) => [...m, { role: 'agent', text: 'Done.' }]);
    } catch {
      setMessages((m) => [...m, { role: 'agent', text: 'Something went wrong reaching the assistant.' }]);
    } finally {
      setBusy(false);
      setLiveSteps([]);
    }
  };

  const startWebSpeech = () => {
    const SR = getSpeech();
    if (!SR) return;
    if (listening) {
      recRef.current?.stop();
      return;
    }
    const rec = new SR();
    rec.lang = 'en-US';
    rec.interimResults = true;
    let finalText = '';
    rec.onresult = (e: any) => {
      let interim = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalText += t;
        else interim += t;
      }
      setInput((finalText + ' ' + interim).trim());
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recRef.current = rec;
    setListening(true);
    rec.start();
  };

  const stopVoice = () => {
    voiceRef.current?.stop();
    voiceRef.current = null;
    setVoiceState(null);
  };

  const startVoice = async () => {
    if (voiceRef.current) {
      stopVoice();
      return;
    }
    setVoiceState('connecting');
    try {
      const res: any = await agentsApi.createBrowserVoiceSession({ agent_key: agentKey });
      const d = res?.data ?? res;
      if (d?.streaming && d?.session_id) {
        const base = String((config as any)?.weaveinApi?.baseUrl || '').replace(/\/$/, '');
        const wsUrl = base.replace(/^http/, 'ws') + `/api/webhooks/voice-agent/browser-stream?session_id=${encodeURIComponent(d.session_id)}`;
        const vs = new BrowserVoiceSession(wsUrl, {
          onCaption: (role, text) => setMessages((m) => [...m, { role: role === 'user' ? 'user' : 'agent', text }]),
          onState: (s) => {
            setVoiceState(s);
            if (s === 'ended') { voiceRef.current = null; setVoiceState(null); }
            if (s === 'error') { voiceRef.current = null; setVoiceState(null); startWebSpeech(); }
          },
        });
        voiceRef.current = vs;
        void vs.start();
      } else {
        setVoiceState(null);
        startWebSpeech();
      }
    } catch {
      setVoiceState(null);
      startWebSpeech();
    }
  };

  const empty = messages.length === 0;

  return (
    <div style={S.wrap}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div ref={threadRef} style={S.thread}>
        {empty && (
          <div style={{ margin: 'auto', textAlign: 'center', color: tvTokens.ink500, padding: '16px 0' }}>
            <Sparkles size={22} color={tvTokens.accentInk} style={{ opacity: 0.7 }} />
            <p style={{ fontSize: 13.5, margin: '10px 0 0' }}>How can I help?</p>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} style={m.role === 'user' ? S.userRow : S.agentRow}>
            <div style={m.role === 'user' ? S.userBubble : S.agentBubble}>
              {m.text}
              {m.pending && m.pending.length > 0 && (
                <div style={S.pending}>
                  <ShieldCheck size={12} /> Needs approval: {m.pending.map((p) => p.tool).join(', ')}
                </div>
              )}
            </div>
          </div>
        ))}
        {busy && (
          <div style={S.agentRow}>
            <div style={{ ...S.agentBubble, display: 'flex', flexDirection: 'column', gap: 5 }}>
              {liveSteps.length === 0 ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> Working…
                </span>
              ) : (
                liveSteps.map((s, j) => (
                  <span key={j} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13 }}>
                    {s.done ? (
                      <Check size={13} color={tvTokens.success || '#1f9663'} />
                    ) : (
                      <Loader2 size={12} style={{ animation: 'spin 1s linear infinite', color: tvTokens.accentInk }} />
                    )}
                    <span style={{ color: s.done ? tvTokens.ink500 : tvTokens.ink }}>{prettyTool(s.tool)}</span>
                  </span>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {empty && examples.length > 0 && (
        <div style={S.examples}>
          {examples.map((ex) => (
            <button key={ex} onClick={() => setInput(ex)} style={S.chip}>
              {ex}
            </button>
          ))}
        </div>
      )}

      {voiceActive && (
        <div style={S.voiceBar}>
          <span style={{ ...S.voiceDot }} />
          <span style={{ flex: 1 }}>
            {voiceState === 'connecting' ? 'Connecting…' : voiceState === 'speaking' ? 'Speaking…' : 'Listening — just talk'}
          </span>
          <button onClick={stopVoice} style={S.voiceStop}>
            <Square size={11} /> Stop
          </button>
        </div>
      )}

      <div style={S.composer}>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') send();
          }}
          placeholder={listening ? 'Listening…' : `Ask ${title.toLowerCase()}…`}
          rows={2}
          style={S.textarea}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button onClick={startVoice} title={voiceActive ? 'Stop voice' : 'Talk'} style={{ ...S.micBtn, ...(voiceActive || listening ? S.micActive : {}) }}>
            {voiceActive ? <Square size={15} /> : <Mic size={18} />}
          </button>
          <button onClick={send} disabled={!input.trim() || busy} style={{ ...S.sendBtn, opacity: !input.trim() || busy ? 0.5 : 1 }}>
            <ArrowUp size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

/** Integrated, in-flow collapsible panel wrapping AgentAssistant. Sits at the top of a
 *  page's content (below the pillar bar) — an integral part of the page like the Design
 *  Studio assistant, not a floating widget, so it never overlaps the support-chat launcher. */
export function AgentAssistantPanel(props: AgentAssistantProps) {
  const [collapsed, setCollapsed] = useState(false);
  return (
    <section style={S.panel} className="tv-agent-panel">
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        aria-expanded={!collapsed}
        style={{ ...S.panelHead, borderBottom: collapsed ? 'none' : `1px solid ${tvTokens.border}` }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13.5, fontWeight: 650, color: tvTokens.ink }}>
          <Sparkles size={15} color={tvTokens.accentInk} /> {props.title}
          {collapsed && <span style={{ fontWeight: 400, color: tvTokens.ink500, fontSize: 12.5 }}>— ask or talk</span>}
        </span>
        <ChevronDown
          size={16}
          style={{ color: tvTokens.ink400 || '#8a93a8', transform: collapsed ? 'rotate(-90deg)' : 'none', transition: 'transform .15s' }}
        />
      </button>
      {!collapsed && (
        <div style={S.panelBody}>
          <AgentAssistant {...props} />
        </div>
      )}
    </section>
  );
}

function prettyTool(t: string): string {
  const map: Record<string, string> = {
    'pipeline.list_opportunities': 'Reading the pipeline',
    'pipeline.advance_stage': 'Advancing the stage',
    'analytics.funnel_summary': 'Summarizing the funnel',
    'analytics.campaign_performance': 'Reading campaign performance',
    'leads.account_scores': 'Scoring accounts',
    'leads.org_lookup': 'Looking up the org',
    'leads.list_audiences': 'Listing audiences',
    'campaign.list_campaigns': 'Listing campaigns',
    'campaign.draft_sequence': 'Drafting a sequence',
    'content.generate_copy': 'Writing copy',
  };
  return map[t] || t;
}

const S: Record<string, React.CSSProperties> = {
  wrap: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 10 },
  thread: { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 10, overflowY: 'auto', padding: '4px 2px' },
  userRow: { display: 'flex', justifyContent: 'flex-end' },
  agentRow: { display: 'flex', justifyContent: 'flex-start' },
  userBubble: { background: tvTokens.accentInk, color: '#fff', padding: '9px 13px', borderRadius: '13px 13px 4px 13px', fontSize: 13.5, maxWidth: '86%', lineHeight: 1.45 },
  agentBubble: { background: tvTokens.paper2, color: tvTokens.ink, border: `1px solid ${tvTokens.border}`, padding: '9px 13px', borderRadius: '13px 13px 13px 4px', fontSize: 13.5, maxWidth: '90%', lineHeight: 1.5 },
  pending: { display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 7, fontSize: 12, color: '#b9781f', background: '#f8efda', padding: '4px 9px', borderRadius: 6 },
  examples: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  chip: { fontSize: 12.5, color: tvTokens.ink500, background: tvTokens.surface || '#fff', border: `1px solid ${tvTokens.border}`, padding: '6px 11px', borderRadius: 999, cursor: 'pointer' },
  voiceBar: { display: 'flex', alignItems: 'center', gap: 9, background: '#f1eafb', border: '1px solid #d9c9f3', borderRadius: 9, padding: '7px 12px', fontSize: 12.5, color: '#5a3a9c', fontWeight: 500 },
  voiceDot: { width: 8, height: 8, borderRadius: '50%', background: '#7a46c9', flex: 'none' },
  voiceStop: { display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 600, color: '#7a46c9', background: '#fff', border: '1px solid #d9c9f3', borderRadius: 6, padding: '4px 9px', cursor: 'pointer' },
  composer: { display: 'flex', alignItems: 'flex-end', gap: 7, background: tvTokens.surface || '#fff', border: `1.5px solid ${tvTokens.border}`, borderRadius: 12, padding: 8 },
  textarea: { flex: 1, border: 'none', outline: 'none', resize: 'none', background: 'transparent', fontFamily: tvTokens.fontSans, fontSize: 14, color: tvTokens.ink, lineHeight: 1.5, padding: '3px 5px' },
  micBtn: { width: 38, height: 38, borderRadius: 10, border: '1px solid #d9c9f3', background: '#f6f1fd', color: '#7a46c9', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
  micActive: { background: '#7a46c9', color: '#fff', borderColor: '#7a46c9' },
  sendBtn: { width: 38, height: 38, borderRadius: 10, border: 'none', background: tvTokens.accentInk, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
  panel: { display: 'flex', flexDirection: 'column', background: tvTokens.surface || '#fff', border: `1px solid ${tvTokens.border}`, borderRadius: 14, marginBottom: 20, overflow: 'hidden', boxShadow: tvTokens.shadow1 || '0 1px 2px rgba(20,22,34,0.05)' },
  panelHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '11px 16px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' },
  panelBody: { display: 'flex', minHeight: 200, maxHeight: '52vh', padding: '12px 16px 14px' },
};

export default AgentAssistant;
